// RecommendedCompSelection is responsible for the overall comp selection user
// flow - allowing the user to query for recommended comps, waiting, and then
// showing comp selection (right now this is a grid).
import {
  AloftDate,
  MLSListing,
  PolygonGeoSearch,
  ZillowPropertySearchResponse,
  isDefined,
  isUndefined,
} from '@aloft/toolkit-shared';

import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react';

import { useEdgeTrigger } from '../../../hooks/useEdgeTrigger.ts';
import { useZodFormContext } from '../../../hooks/useZodForm.ts';
import { countActiveFilters } from '../../FilterDrawer/countActiveFilters.ts';
import { LoadingScreenSam } from '../../LoadingScreen/LoadingScreenSam.tsx';
import { useRecommendedProperties } from '../hooks/useRecommendedProperties.ts';
import { useSubjectPropertyToCompSelectionProperty } from '../hooks/useSubjectPropertyToCompSelectionProperty.ts';
import {
  CompSearch,
  CompSelectionProperty,
  RecommendCompsFormSchema,
  RecommendedCompSelectionProps,
} from '../types.ts';
import { compsToCompSelectionProps } from '../utils/compsToCompSelectionProps.ts';
import { convertRecommendedCompsToCSV } from '../utils/convertRecommendedCompsToCSVData.ts';
import { isWithinDistanceMiles } from '../utils/isWithinDistanceMiles.ts';
import { listingsToCompSelectionProps } from '../utils/listingsToCompSelectionProps.ts';
import { zillowPropsToCompSelectionProps } from '../utils/zillowPropsToCompSelectionProps.ts';
import { CompSelection } from './CompSelection.tsx';
import { MissingDataWarning } from './MissingDataWarning.tsx';
import { NoRecommendationsAvailable } from './NoRecommendationsAvailable.tsx';
import { getQualityTierBreakdownData } from './qualityTier/getQualityTierBreakdownData.ts';

type ListingData =
  | {
      source: 'constellation1';
      listings: MLSListing[];
    }
  | {
      source: 'abode';
      listings: MLSListing[];
    }
  | {
      source: 'z';
      listings: ZillowPropertySearchResponse[];
    };

const NEARBY_MILES_THRESHOLD = 100;
const MAX_COMPS = 99;

const RecCompSelectionInner = forwardRef(
  (
    {
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      listingData,
      subjectComp,
      comps,
      orderID,
      isInspectifyOrder,
      hasArvOption,
    }: Pick<
      RecommendedCompSelectionProps,
      'sorting' | 'setSorting' | 'rowSelection' | 'setRowSelection' | 'orderID'
    > & {
      subjectComp: CompSelectionProperty;
      comps: CompSelectionProperty[];
      listingData: ListingData;
      isInspectifyOrder: boolean;
      hasArvOption: boolean;
    },
    ref,
  ) => {
    const subjectCoordinates =
      subjectComp.latitude && subjectComp.longitude
        ? { lat: subjectComp.latitude, lng: subjectComp.longitude }
        : undefined;

    const recommendations = useMemo(
      () =>
        [
          ...(listingData.source === 'constellation1' || listingData.source === 'abode'
            ? listingsToCompSelectionProps(subjectComp, comps, listingData.listings, hasArvOption)
            : zillowPropsToCompSelectionProps(
                subjectCoordinates,
                subjectComp,
                comps,
                listingData.listings,
              )),
        ]
          .filter((rec) => {
            switch (rec.mlsStatus?.toLowerCase()) {
              case 'active':
              case 'pending':
              case 'sold':
              case 'recently_sold':
                return true;
              default:
                return false;
            }
          })
          .sort(
            (a, b) =>
              (b.similarityScore ?? Number.MIN_VALUE) - (a.similarityScore ?? Number.MIN_VALUE),
          ),
      [listingData, subjectComp, comps, subjectCoordinates, hasArvOption],
    );

    // If enough comps are nearby subject, filter out comps that aren't nearby
    const filteredRecs = useMemo(
      () =>
        (recommendations.filter(isWithinDistanceMiles(NEARBY_MILES_THRESHOLD)).length >= MAX_COMPS
          ? recommendations.filter(isWithinDistanceMiles(NEARBY_MILES_THRESHOLD))
          : recommendations
        ).filter(
          // Hack - simple but incomplete equality checker to filter out subject listing
          (comp) =>
            comp.address?.AddressLineText !== subjectComp.address?.AddressLineText &&
            (listingData.source === 'z' || comp.listingID !== subjectComp.listingID),
        ),
      [recommendations],
    );

    const dataWithSelectedComps = useMemo(() => {
      const seen = new Set<string>();
      const makeKey = (comp: CompSelectionProperty) =>
        (
          comp.listingID ??
          comp.address?.AddressLineText?.trim().toLowerCase() ??
          comp.id
        ).toLowerCase();
      const uniqueFilteredRecs = filteredRecs.filter((comp) => {
        const key = makeKey(comp);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const missingSelectedComps = comps.filter((comp) => !seen.has(makeKey(comp)));

      return [...uniqueFilteredRecs, ...missingSelectedComps];
    }, [comps, filteredRecs]);

    const downloadCSV = () => {
      const csvContent = convertRecommendedCompsToCSV(dataWithSelectedComps);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `recommended_comps_order_${orderID}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    };

    const getQualityTiersBreakdown = () => getQualityTierBreakdownData(dataWithSelectedComps);

    useImperativeHandle(ref, () => ({
      downloadCSV,
      getQualityTiersBreakdown,
    }));

    return (
      <CompSelection
        sorting={sorting}
        setSorting={setSorting}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        data={dataWithSelectedComps.slice(0, MAX_COMPS)}
        subject={subjectComp}
        comps={comps}
        source={listingData.source}
        orderID={orderID}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
      />
    );
  },
);

export const RecommendedCompSelection = forwardRef(
  (
    {
      orderID,
      subject,
      effectiveDate,
      comps,
      compSeq,
      getDefaultFormValues,
      showNoRecommendations,
      setShowNoRecommendations,
      sorting,
      setSorting,
      rowSelection,
      setRowSelection,
      isInspectifyOrder,
      hasArvOption,
      allowRecommendedCompsFiltering,
      onClose,
    }: RecommendedCompSelectionProps,
    ref,
  ) => {
    const effectiveDateAloft = AloftDate.fromIsoString(effectiveDate.isoString);
    const { isIdle, isLoading, isError, data, getProperties } = useRecommendedProperties({
      orderID,
    });
    const subjectComp = useSubjectPropertyToCompSelectionProperty(subject, effectiveDateAloft);

    const isSubjectGlaDefined = isDefined(subject.editableData.improvements.gla);
    const isSubjectLotSizeDefined =
      isDefined(subject.editableData.site.area) && subject.editableData.site.area.unitAmount > 0;
    const isSubjectLocationDefined =
      isDefined(subjectComp.latitude) && isDefined(subjectComp.longitude);
    const { getValues, setValue } = useZodFormContext<RecommendCompsFormSchema>();

    const isRunnable = isSubjectGlaDefined && isSubjectLotSizeDefined && isSubjectLocationDefined;

    const getSearchCriteria = () => {
      const compSearch = getValues().search as CompSearch;
      const coordinates = (getValues().search.geoSearch as PolygonGeoSearch).coords;
      return { search: compSearch, coordinates };
    };

    useEdgeTrigger({
      condition: !isLoading && isIdle && isRunnable && isUndefined(data),
      action: () => {
        const { search } = getValues();
        if (countActiveFilters(search.filters) === 0) {
          const formSearch = getDefaultFormValues();
          setValue('search', formSearch);
        }
        setShowNoRecommendations(false);
        getProperties(getSearchCriteria());
      },
    });

    useEffect(() => {
      if (data?.body.status === 'not_found' || data?.body.status === 'error') {
        setShowNoRecommendations(true);
      } else {
        setShowNoRecommendations(false);
      }
    }, [data?.body.status, setShowNoRecommendations]);

    // If the attachment is not_found or errored show some screen to relay that info
    if (
      showNoRecommendations &&
      (data?.body.status === 'not_found' || data?.body.status === 'error') &&
      !isInspectifyOrder
    ) {
      return (
        <NoRecommendationsAvailable
          body={data?.body}
          getProperties={() => {
            if (allowRecommendedCompsFiltering) {
              setShowNoRecommendations(false);
            } else {
              getProperties(getSearchCriteria());
              setShowNoRecommendations(false);
            }
          }}
        />
      );
    }
    if (isError) {
      return (
        <NoRecommendationsAvailable
          body={{ status: 'error', message: 'An internal error occurred.' }}
          getProperties={() => {
            setShowNoRecommendations(false);
            getProperties(getSearchCriteria());
          }}
        />
      );
    }

    if (isLoading || isUndefined(data)) {
      // If we can't run comps because we are missing data show a screen to tell the user
      if (!isLoading && !isRunnable && isUndefined(data)) {
        return (
          <MissingDataWarning
            isSubjectGlaDefined={isSubjectGlaDefined}
            isSubjectLotSizeDefined={isSubjectLotSizeDefined}
            isSubjectLocationDefined={isSubjectLocationDefined}
            onClose={onClose}
          />
        );
      }
      // If we are loading the attachment / account info show a loading screen
      return <LoadingScreenSam overrideText="Loading recommended comps..." />;
    }

    const convertedComps = compsToCompSelectionProps(compSeq, comps, subjectComp);

    let listingData: ListingData;
    let results: MLSListing[] | ZillowPropertySearchResponse[] = [];

    if (data.body.status === 'found') {
      if ('output' in data.body && 'results' in data.body.output) {
        results = data.body.output.results;
      }
    }

    if (data.source === 'constellation1' || data.source === 'abode') {
      listingData = {
        source: data.source,
        listings: results as MLSListing[],
      };
    } else if (data.source === 'z') {
      listingData = {
        source: data.source,
        listings: results as ZillowPropertySearchResponse[],
      };
    } else {
      throw new Error('Invalid source');
    }

    return (
      <RecCompSelectionInner
        sorting={sorting}
        setSorting={setSorting}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        listingData={listingData}
        subjectComp={subjectComp}
        comps={convertedComps.filter(isDefined)}
        orderID={orderID}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        ref={ref}
      />
    );
  },
);
